// @flow
import fp from "lodash/fp"
import _ from "lodash"
import { omit } from "ramda"

import { InputProps } from "common/consts/form"
import { FIELD_VALIDATION_ERRORS, SUPPORTED_ATTACHMENTS } from "./BookingFileUploadConsts"

// VALIDATION
export const validateFile = (file: File, sizeLimit?: number = 10000000): ?string => {
  let err = null
  const parts = file.name.split(".")
  const extension = parts[parts.length - 1]
  if (file.size > sizeLimit) {
    err = FIELD_VALIDATION_ERRORS.FILE_TOO_LARGE
  } else if (!SUPPORTED_ATTACHMENTS.includes(extension.toLowerCase())) {
    err = FIELD_VALIDATION_ERRORS.UNSUPPORTED_EXTENSION
  }
  return err
}

const isNotString = fp.negate(fp.isString)
const hasFile = value => isNotString(value) && (value && value.length !== 0)
const getLastField = (fields: Array<FileList | Array<File>>, fieldName: number) =>
  fields[fieldName][fields[fieldName].length - 1]

const filesToArray = (files: Array<Array<File> | FileList>): Array<File> => {
  if (!files) return []
  const flatFiles = []
  files.forEach(fileList => {
    if (fileList) {
      for (let i = 0; i < fileList.length; i++) {
        // if there was removal from FileList it is an array
        // $FlowFixMe - FileList has .item, Array does not
        const file = fileList.item ? fileList.item(i) : fileList[i]
        flatFiles.push(file)
      }
    }
  })

  return flatFiles
}

// Helper function for uploads
export const getLastFileFieldProps = fp.compose(
  fp.pick(InputProps),
  getLastField,
)
export const getLastFileFieldKey = fp.compose(
  fp.get("name"),
  getLastField,
)
export const hasLastFieldFile = fp.compose(
  hasFile,
  fp.get("value"),
  getLastField,
)

export const getAmountOfFiles = (fileBatches: Array<FileList | Array<File>>) =>
  _.reduce(
    fileBatches,
    (acc, fileList) => {
      if (fileList && fileList.length) {
        return acc + fileList.length
      }
      return acc
    },
    0,
  )

// return object where key is file id and value is string describing error or null
// eslint-disable-next-line flowtype/no-weak-types
export const validateFiles = (
  fileBatches: Array<FileList | Array<File>>,
  sizeLimit?: number = 10000000,
): ?string => {
  let totalFilesSize = 0
  let err = null
  _.forEach(fileBatches, fileList => {
    if (!fileList || err) return
    for (let i = 0; i < fileList.length; i++) {
      // if there was removal from FileList it is an array
      // $FlowFixMe - FileList has .item, Array does not
      const file = fileList.item ? fileList.item(i) : fileList[i]
      totalFilesSize += file.size
      err = validateFile(file, sizeLimit) // set to null or string
    }
  })
  if (totalFilesSize >= sizeLimit) {
    err = FIELD_VALIDATION_ERRORS.FILES_TOO_LARGE
  }
  return err
}

export function getFileURIs(files: Array<Array<File> | FileList>) {
  if (!files) return []
  const flatFiles = filesToArray(files)

  const URIPromises = _.map(
    flatFiles,
    file =>
      new Promise(resolve => {
        const reader = new FileReader()
        reader.onload = upload => resolve(upload.target.result)
        reader.readAsDataURL(file)
      }),
  )

  return Promise.all(URIPromises)
}

export const getFileNames = (
  files: Array<Array<File> | FileList>,
): Array<{ name: string, size: number }> => {
  if (!files) return []

  const flatFiles = filesToArray(files)

  return flatFiles.reduce((acc, file) => {
    acc.push({
      name: file.name,
      size: file.size,
    })
    return acc
  }, [])
}

export const cleanFilesForPayloadTracking = (payload: any) => {
  if (typeof payload === "object" && payload.files) {
    const omittedPayload = omit(["files"], payload)

    if (payload.files.size) {
      return {
        ...omittedPayload,
        files: `Submitted ${payload.files.size} file(s). Omitted for tracking.`,
      }
    }

    return omittedPayload
  }

  return payload
}
